import {getTestProps} from '@benchling/js-test-utils';
import {Box, Button, createStyles, Stack, Text} from '@mantine/core';
import React, {useCallback, useEffect, useMemo, useRef, useState} from 'react';

import {trackEvent} from 'analytics/Analytics';
import {LLM_CHAT_ANALYTICS_ACTIONS} from 'constants/analytics-products/LLMChatConstants';
import {AgentType} from 'graphql-client/__generated/graphqlGlobalTypes';
import * as ChatDAO from 'llm/chat-library/ChatDAO';
import {useMessageThread} from 'llm/chat-library/useMessageThread';
import {CenteredSpinner} from 'ui-library/Spinner';
import * as Net from 'util/Net';

import {OnboardingSkipButton} from './OnboardingComponents';
import {OnboardingQuestionView, OnboardingScreenShell} from './OnboardingQuestionView';

/** Supporting copy shown under every onboarding question heading. */
const ONBOARDING_SUBTITLE = 'Improve AI responses by sharing details about yourself';

interface OnboardingQuestion {
  question: string;
  choices: Array<string>;
}

interface OnboardingAppState {
  is_complete?: boolean;
  questions_asked?: Array<OnboardingQuestion>;
}

function isOnboardingAppState(state: unknown): state is OnboardingAppState {
  if (!state || typeof state !== 'object') {
    return false;
  }
  const s = state as OnboardingAppState;
  return s.questions_asked != null || s.is_complete != null;
}

const SKIPPED_ANSWER = '[skipped]';

const useStyles = createStyles((theme) => ({
  body: {
    flex: 1,
    overflowY: 'auto',
  },
  loadingState: {
    padding: `${theme.spacing.lg} 0`,
  },
  loadingText: {
    fontSize: theme.fontSizes.sm,
    color: theme.colors.gray[6],
    textAlign: 'center',
  },
  loadingAnswerText: {
    fontSize: theme.fontSizes.sm,
    color: theme.colors.gray[6],
    textAlign: 'center',
    maxWidth: 280,
    wordWrap: 'break-word',
  },
  errorText: {
    fontSize: theme.fontSizes.sm,
    color: theme.colors.gray[7],
    textAlign: 'center',
  },
}));

export function CyclingAnswersText({answers}: {answers: Array<string>}): JSX.Element | null {
  const {classes} = useStyles();
  const [index, setIndex] = useState(0);

  useEffect(() => {
    setIndex(0);
    if (answers.length <= 1) {
      return;
    }
    const id = setInterval(() => {
      setIndex((i) => (i + 1) % answers.length);
    }, 1800);
    return () => {
      clearInterval(id);
    };
  }, [answers]);

  if (answers.length === 0) {
    return null;
  }

  return <Text className={classes.loadingAnswerText}>"{answers[index]}"</Text>;
}

function QuestionView({
  chatSessionId,
  questions,
  questionGroupIndex,
  progressCount,
  onAnswersSubmitted,
  onQuestionIndexChange,
  onQuestionAnswered,
}: {
  chatSessionId: string;
  questions: Array<OnboardingQuestion>;
  /** Zero-based index of this question group within the entire onboarding flow (increments each time the backend sends a new batch). */
  questionGroupIndex: number;
  /** Steps reached so far (across all batches), used to fill the progress bar. */
  progressCount: number;
  onAnswersSubmitted: (answers: Array<string>) => void;
  onQuestionIndexChange: (index: number) => void;
  /** Fired once per question the user resolves (answer or Skip), so the parent can tally progress. */
  onQuestionAnswered: () => void;
}): JSX.Element {
  const [batchIndex, setBatchIndex] = useState(0);

  useEffect(() => {
    onQuestionIndexChange(batchIndex);
  }, [batchIndex, onQuestionIndexChange]);
  const [batchAnswers, setBatchAnswers] = useState<Array<string>>([]);
  // Multi-select: chips toggle independently and combine with the free-text value into one answer.
  // The user confirms with Continue (chips do not auto-advance), so several options can be chosen.
  const [selectedChoices, setSelectedChoices] = useState<Set<string>>(new Set());
  const [freeTextValue, setFreeTextValue] = useState('');

  const [isSubmitting, setIsSubmitting] = useState(false);

  const currentQuestion = questions[batchIndex] ?? null;
  const freeTextTrimmed = freeTextValue.trim();
  // Selected chips and the free-text value combine into a single answer string for this question.
  const effectiveAnswer = [...selectedChoices, ...(freeTextTrimmed ? [freeTextTrimmed] : [])].join(', ');
  const canContinue = !isSubmitting && effectiveAnswer.length > 0;

  const handleSelectChoice = useCallback((choice: string) => {
    setSelectedChoices((prev) => {
      const next = new Set(prev);
      if (next.has(choice)) {
        next.delete(choice);
      } else {
        next.add(choice);
      }
      return next;
    });
  }, []);

  const handleDiscardFreeText = useCallback(() => {
    setFreeTextValue('');
  }, []);

  const submitAnswers = useCallback(
    async (answers: Array<string>) => {
      setIsSubmitting(true);
      onAnswersSubmitted(answers);
      try {
        const messageText = questions.map((q, i) => `${q.question}: ${answers[i] ?? ''}`).join('\n');
        await ChatDAO.appendDisplayMessageToChatSession({
          chatSessionId,
          agentType: AgentType.ONBOARDING,
          messageBlocks: [{type: 'text', text: messageText}],
          userChatContext: ChatDAO.getUserChatContext({contextIds: []}),
          sources: [],
        });
      } catch (err) {
        if (!Net.isServerRequestError(err)) {
          throw err;
        }
        setIsSubmitting(false);
      }
    },
    [chatSessionId, questions, onAnswersSubmitted]
  );

  const handleAdvance = useCallback(
    async (answer: string) => {
      const isSkipped = answer === SKIPPED_ANSWER;

      if (isSkipped) {
        trackEvent(LLM_CHAT_ANALYTICS_ACTIONS.SKIPPED_ONBOARDING_QUESTION, {
          question_index_in_group: batchIndex,
          question_group_index: questionGroupIndex,
          question_group_size: questions.length,
          num_choices: currentQuestion.choices.length,
        });
      } else {
        // The answer may combine several picked chips and/or a free-text value.
        const hasFreeText = freeTextValue.trim().length > 0;
        const selectedChoiceCount = selectedChoices.size;
        trackEvent(LLM_CHAT_ANALYTICS_ACTIONS.ANSWERED_ONBOARDING_QUESTION, {
          question_index_in_group: batchIndex,
          question_group_index: questionGroupIndex,
          question_group_size: questions.length,
          num_choices: currentQuestion.choices.length,
          selected_choice_count: selectedChoiceCount,
          has_free_text_answer: hasFreeText,
          total_answer_count: selectedChoiceCount + (hasFreeText ? 1 : 0),
        });
      }

      // Tally one resolved question (answered or skipped) toward the progress bar.
      onQuestionAnswered();

      const updatedAnswers = [...batchAnswers, answer];
      const nextIndex = batchIndex + 1;

      if (nextIndex < questions.length) {
        setBatchAnswers(updatedAnswers);
        setBatchIndex(nextIndex);
        setSelectedChoices(new Set());
        setFreeTextValue('');
        return;
      }

      await submitAnswers(updatedAnswers);
    },
    [
      batchAnswers,
      batchIndex,
      questions,
      questionGroupIndex,
      freeTextValue,
      selectedChoices,
      submitAnswers,
      currentQuestion,
      onQuestionAnswered,
    ]
  );

  if (!currentQuestion) {
    return <></>;
  }

  return (
    <OnboardingQuestionView
      testComponent="AIOnboardingScreen"
      question={currentQuestion.question}
      subtitle={ONBOARDING_SUBTITLE}
      progressCount={progressCount}
      choices={currentQuestion.choices.map((choice) => ({
        label: choice,
        selected: selectedChoices.has(choice),
        // Chips toggle independently so the user can pick several; Continue submits the combined answer.
        onSelect: () => {
          if (isSubmitting) {
            return;
          }
          handleSelectChoice(choice);
        },
      }))}
      freeText={{
        value: freeTextValue,
        onChange: setFreeTextValue,
        onDiscard: handleDiscardFreeText,
      }}
      footer={
        <Stack spacing="sm" align="center">
          {/* Continue submits the combined chips + free-text answer once at least one is present. */}
          <Button
            variant="primary"
            size="sm"
            radius="xl"
            onClick={() => effectiveAnswer && handleAdvance(effectiveAnswer)}
            disabled={!canContinue}
            {...getTestProps({component: 'AIOnboardingScreen', element: 'continue-button'})}
          >
            Continue
          </Button>
          <OnboardingSkipButton
            onClick={() => handleAdvance(SKIPPED_ANSWER)}
            disabled={isSubmitting}
            testComponent="AIOnboardingScreen"
          />
        </Stack>
      }
    />
  );
}

/**
 * Onboarding screen driven directly by useMessageThread.
 *
 * Thread status drives all UI states:
 *  - isAssistantRunning (QUEUED/ACTIVE) → loading spinner
 *  - isAwaitingUserInput (AWAITING_USER_INPUT) → question UI sourced from latestApplicationState
 *  - errorMessage → error + skip UI
 */
export function AIOnboardingScreen({
  chatSessionId,
  onComplete,
}: {
  chatSessionId: string;
  /**
   * Called when the agent survey finishes — either by completing naturally or by the user exiting
   * early. Receives the number of questions answered so far so the next onboarding step (e.g. the
   * interest/first-chat screen) can continue the progress dots. Analytics for completion vs.
   * dismissal are fired before this is invoked.
   */
  onComplete: (answeredCount: number) => void;
}): JSX.Element {
  const {classes} = useStyles();
  const {
    isLoading,
    isAssistantRunning,
    isAwaitingUserInput,
    isCompleted,
    latestApplicationState,
    errorMessage,
  } = useMessageThread({chatSessionId});

  const appState = isOnboardingAppState(latestApplicationState) ? latestApplicationState : null;

  const [submittedAnswers, setSubmittedAnswers] = useState<Array<string>>([]);

  // Number of questions the user has resolved (answered or skipped) across all batches. Held here so
  // it accumulates even as QuestionView remounts per backend batch; drives the progress dots.
  const [answeredCount, setAnsweredCount] = useState(0);
  const handleQuestionAnswered = useCallback(() => {
    setAnsweredCount((prev) => prev + 1);
  }, []);

  // Track how many question batches have been shown (increments each time a new batch arrives).
  const [questionGroupIndex, setQuestionGroupIndex] = useState(0);
  const lastQuestionsRef = useRef<Array<OnboardingQuestion> | null>(null);
  const totalQuestionsShownRef = useRef(0);
  const currentQuestionIndexInGroupRef = useRef(0);

  const handleQuestionIndexChange = useCallback((index: number) => {
    currentQuestionIndexInGroupRef.current = index;
  }, []);

  // Questions are available in app state while AWAITING_USER_INPUT.
  const currentQuestions = useMemo((): Array<OnboardingQuestion> | null => {
    if (!isAwaitingUserInput || !appState?.questions_asked?.length) {
      return null;
    }
    return appState.questions_asked;
  }, [isAwaitingUserInput, appState]);

  // Increment the question group counter when a new batch of questions arrives.
  useEffect(() => {
    if (currentQuestions == null || currentQuestions === lastQuestionsRef.current) {
      return;
    }
    if (lastQuestionsRef.current != null) {
      setQuestionGroupIndex((prev) => prev + 1);
    }
    totalQuestionsShownRef.current += currentQuestions.length;
    currentQuestionIndexInGroupRef.current = 0;
    lastQuestionsRef.current = currentQuestions;
  }, [currentQuestions]);

  // Track when the onboarding screen is first shown.
  useEffect(() => {
    trackEvent(LLM_CHAT_ANALYTICS_ACTIONS.VIEWED_ONBOARDING_SCREEN);
  }, []);

  // When the thread completes, fire a completion event then advance the onboarding flow.
  useEffect(() => {
    if (isCompleted) {
      trackEvent(LLM_CHAT_ANALYTICS_ACTIONS.COMPLETED_ONBOARDING);
      onComplete(answeredCount);
    }
    // answeredCount is intentionally read at completion time only; including it would re-fire on
    // every answer.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isCompleted, onComplete]);

  const handleDismiss = useCallback(() => {
    if (!isCompleted) {
      trackEvent(LLM_CHAT_ANALYTICS_ACTIONS.DISMISSED_ONBOARDING_SCREEN, {
        question_group_index: questionGroupIndex,
        question_index_in_group: currentQuestionIndexInGroupRef.current,
        questions_shown_total: totalQuestionsShownRef.current,
      });
    }
    onComplete(answeredCount);
  }, [isCompleted, onComplete, questionGroupIndex, answeredCount]);

  const showLoading = isLoading || isAssistantRunning || isCompleted;
  const hasPreviousQuestions = (appState?.questions_asked?.length ?? 0) > 0;
  const loadingMessage = hasPreviousQuestions
    ? 'Processing your answers…'
    : 'Starting onboarding and gathering information…';

  return (
    <OnboardingScreenShell testComponent="AIOnboardingScreen" onExit={handleDismiss}>
      {showLoading && (
        <Box className={classes.body}>
          <Stack align="center" spacing="sm" className={classes.loadingState}>
            <CenteredSpinner />
            <Text className={classes.loadingText}>{loadingMessage}</Text>
            {submittedAnswers.length > 0 && <CyclingAnswersText answers={submittedAnswers} />}
          </Stack>
        </Box>
      )}

      {!showLoading && errorMessage && (
        <Box className={classes.body}>
          <Stack align="center" spacing="sm" className={classes.loadingState}>
            <Text className={classes.errorText}>{errorMessage}</Text>
            <Button
              variant="ghost"
              size="sm"
              onClick={handleDismiss}
              {...getTestProps({component: 'AIOnboardingScreen', element: 'error-exit-button'})}
            >
              Exit
            </Button>
          </Stack>
        </Box>
      )}

      {!showLoading && !errorMessage && isAwaitingUserInput && currentQuestions && (
        <QuestionView
          chatSessionId={chatSessionId}
          questions={currentQuestions}
          questionGroupIndex={questionGroupIndex}
          // Count the question currently on screen as a reached step, so the bar shows from step 1.
          progressCount={answeredCount + 1}
          onAnswersSubmitted={setSubmittedAnswers}
          onQuestionIndexChange={handleQuestionIndexChange}
          onQuestionAnswered={handleQuestionAnswered}
        />
      )}
    </OnboardingScreenShell>
  );
}
